import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Upload, X, Trash2, Search, FileText, Send, CheckCircle, ListTodo, Pencil, Save } from "lucide-react";
import UnderwritingReportModal from "./UnderwritingReportModal";
import CaseTasks from "./CaseTasks";
import AccelerationCard from "./AccelerationCard";
import AccelerationConfirmModal from "./AccelerationConfirmModal";
import { formsCatalog } from "./forms/formsCatalog";

import { CONFIG } from "../../shared/config/constants";
import {
  createPnlItem,
  deletePnlItem,
  fetchCaseCollaboration,
  fetchPnlItems,
  fetchPnlSummary,
  createCaseCollaboratorInvite,
  revokeCaseCollaborator,
  updatePnlItem,
  fetchTeamMembers,
  assignMemberToCase,
  fetchCaseTeamAccess,
  removeMemberFromCase,
  fetchRunReport,
  updateCase
} from "../api";
import { accelerateDelivery } from "../api/acceleration";
import { getAccelerationOptions, calculateDeliveryTime } from "../utils/accelerationHelpers";
import { validateFiles, sanitizeFile } from "../../shared/utils/fileValidation";
import DocumentsWidget from "./CaseDetails/DocumentsWidget";
import CommentsWidget from "./CaseDetails/CommentsWidget";
import FormsTab from "./CaseDetails/FormsTab";

const CaseDetailsModal = ({
  selectedCase,
  onClose,
  onStatusChange,
  onDocumentUpload,
  uploadMessage,
  caseFiles,
  isUploading,
  isStatusUpdating,
  handlePreview,
  aiReports,
  aiMessage,
  onGenerateAi,
  isAiGenerating,
  caseComments,
  onAddComment,
  newComment,
  setNewComment,
  isCommenting,
  showSampleReport,
  setShowSampleReport,
  autoDownloadSample,
  setAutoDownloadSample,
  isCaseLoading,
  uploadProgress,
  onDeleteFile,
  isDeletingFile,
  fileDeleteTarget,
  isCaseHydrating = false,
  onUpgradePlan,
  featureFlags = {},
  isAccountSettingsOpen = false,
  aiPipelineStatus = null,
  caseError,
  pipelineRuns = [],
  userTier = 'free',
  userCredits = 0,
  onRefreshCredits,
  onCaseUpdate,
}) => {

  const [reportData, setReportData] = useState(null);
  const [isFetchingReport, setIsFetchingReport] = useState(null); // Stores the run ID being fetched
  const [pnlItems, setPnlItems] = useState([]);
  const [pnlSummary, setPnlSummary] = useState(null);
  const [isPnlLoading, setIsPnlLoading] = useState(false);
  const [isPnlSaving, setIsPnlSaving] = useState(false);
  const [pnlError, setPnlError] = useState("");
  const [editingPnlItem, setEditingPnlItem] = useState(null);
  const [newPnlItem, setNewPnlItem] = useState({ description: "", amount: "", type: "revenue" });
  const [activeTab, setActiveTab] = useState("information");
  const [modalSize, setModalSize] = useState("default");
  const [accelerationOption, setAccelerationOption] = useState(null);
  const [isAccelerationModalOpen, setIsAccelerationModalOpen] = useState(false);
  const [isAccelerating, setIsAccelerating] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [isAddingFormId, setIsAddingFormId] = useState("");

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [editedCase, setEditedCase] = useState({
    name: "",
    propertyAddress: "",
    loanType: "",
    loanAmount: "",
    email: ""
  });

  // Initialize edited case when selectedCase changes
  useEffect(() => {
    if (selectedCase) {
      setEditedCase({
        name: selectedCase.name || "",
        propertyAddress: selectedCase.propertyAddress || "",
        loanType: selectedCase.loanType || "",
        loanAmount: selectedCase.loanAmount || "",
        email: selectedCase.email || ""
      });
    }
  }, [selectedCase]);

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel - reset to original values
      setEditedCase({
        name: selectedCase.name || "",
        propertyAddress: selectedCase.propertyAddress || "",
        loanType: selectedCase.loanType || "",
        loanAmount: selectedCase.loanAmount || "",
        email: selectedCase.email || ""
      });
      setUpdateError("");
    }
    setIsEditMode(!isEditMode);
  };

  // Handle case update
  const handleCaseUpdate = async () => {
    if (!selectedCase?.id) return;

    setIsUpdating(true);
    setUpdateError("");

    try {
      // Only send changed fields
      const updates = {};
      if (editedCase.name !== selectedCase.name) updates.name = editedCase.name;
      if (editedCase.propertyAddress !== selectedCase.propertyAddress) updates.propertyAddress = editedCase.propertyAddress;
      if (editedCase.loanType !== selectedCase.loanType) updates.loanType = editedCase.loanType;
      if (editedCase.loanAmount !== selectedCase.loanAmount) updates.loanAmount = editedCase.loanAmount;
      if (editedCase.email !== selectedCase.email) updates.email = editedCase.email;

      if (Object.keys(updates).length === 0) {
        setIsEditMode(false);
        return;
      }

      const result = await updateCase({
        caseId: selectedCase.id,
        ...updates
      });

      // Merge updates with current case data for parent update
      const updatedCaseData = { ...selectedCase, ...updates, ...(result.data || {}) };

      // Update local editedCase state with new values
      setEditedCase({
        name: updatedCaseData.name || "",
        propertyAddress: updatedCaseData.propertyAddress || "",
        loanType: updatedCaseData.loanType || "",
        loanAmount: updatedCaseData.loanAmount || "",
        email: updatedCaseData.email || ""
      });

      // Notify parent of the update
      if (onCaseUpdate) {
        onCaseUpdate(updatedCaseData);
      }

      setIsEditMode(false);
    } catch (error) {
      setUpdateError(error.message || "Failed to update case");
    } finally {
      setIsUpdating(false);
    }
  };

  const existingFormNames = new Set(
    (caseFiles?.[selectedCase?.id] || []).map((file) => (file.name || "").toLowerCase())
  );

  const handleAddForm = async (form) => {
    if (!form || !onDocumentUpload || !selectedCase?.id) return;
    const fileName =
      form.fileName ||
      `${form.name.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "")}.pdf`;
    if (existingFormNames.has(fileName.toLowerCase())) return;
    setIsAddingFormId(form.id);
    try {
      const response = await fetch(form.file);
      if (!response.ok) {
        throw new Error(`Failed to fetch form template (${response.status})`);
      }
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: form.type || "application/pdf" });
      onDocumentUpload({ target: { files: [file], value: "" } });
    } catch (error) {
      console.error("Unable to add form template", error);
      alert("Unable to add form template. Please try again.");
    } finally {
      setIsAddingFormId("");
    }
  };

  const handleNewPnlItemChange = (e) => {
    const { name, value } = e.target;
    setNewPnlItem(prev => ({ ...prev, [name]: value }));
  };

  const handleViewAiReport = async (item, options = {}) => {
    const runId = item.run_id || item.id;
    if (!runId) {
      alert("No Run ID associated with this item");
      return;
    }

    setIsFetchingReport(runId); // Set to the specific run ID being fetched
    setReportData(null);
    setAutoDownloadSample(Boolean(options.download));

    try {
      const caseId = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
      if (!caseId) throw new Error("Missing case id.");

      console.log("[AI Report] Fetching report for:", { caseId, runId, item });

      const response = await fetchRunReport(caseId, runId);
      const reportJson = response?.data?.data || response?.data || response;

      console.log("[AI Report] JSON payload:", reportJson);

      if (!reportJson || Object.keys(reportJson).length === 0) {
        throw new Error("Report is empty or not ready yet");
      }

      setReportData(reportJson);
      setShowSampleReport(true);
    } catch (err) {
      console.error("Error viewing report:", err);

      // Check if it's a 404 error (report not found)
      const errorMsg = err.message || err.toString() || "Unknown error";
      const isNotFound = errorMsg.includes("404") || errorMsg.includes("not found") || errorMsg.includes("ReportNotFound");

      if (isNotFound) {
        alert(`Report Not Available\n\nThe AI analysis run has completed, but the report hasn't been generated yet. This can happen when:\n\n• The report is still being processed\n• The report generation failed\n• The run completed without generating a report\n\nPlease try again in a few moments, or contact support if this persists.`);
      } else {
        alert(`Failed to view report: ${errorMsg}\n\nPlease check the browser console for more details.`);
      }
    } finally {
      setIsFetchingReport(null); // Clear the fetching state
    }
  };

  const normalizePnlAmount = (amount, type) => {
    const numeric = parseFloat(amount);
    if (Number.isNaN(numeric)) return null;
    return type === "expense" ? -Math.abs(numeric) : Math.abs(numeric);
  };

  const normalizePnlInputValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).replace(/[^0-9.-]/g, "");
  };

  const unwrapApiPayload = (response) => response?.data?.data || response?.data || {};
  const extractPnlItems = (payload) => {
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.data?.data?.items)) return payload.data.data.items;
    return [];
  };
  const extractPnlItem = (payload) => {
    if (payload?.id) return payload;
    if (payload?.data) return extractPnlItem(payload.data);
    return payload || {};
  };
  const extractPnlSummary = (payload) => {
    if (!payload) return null;
    if (payload.totalRevenue !== undefined || payload.net !== undefined) return payload;
    if (payload.data) return extractPnlSummary(payload.data);
    return null;
  };

  const refreshPnlSummary = async (caseId, shouldUpdate = true) => {
    try {
      const summaryResponse = await fetchPnlSummary(caseId);
      if (shouldUpdate) {
        const summaryPayload = unwrapApiPayload(summaryResponse);
        setPnlSummary(extractPnlSummary(summaryPayload));
      }
    } catch (error) {
      if (shouldUpdate) {
        setPnlSummary(null);
      }
    }
  };

  const pnlCaseId = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;

  const handleAddNewPnlItem = async (e) => {
    e.preventDefault();
    if (!newPnlItem.description || !newPnlItem.amount) return;
    if (!pnlCaseId) return;
    const finalAmount = normalizePnlAmount(newPnlItem.amount, newPnlItem.type);
    if (finalAmount === null) return;

    setIsPnlSaving(true);
    setPnlError("");
    try {
      const response = await createPnlItem({
        caseId: pnlCaseId,
        description: newPnlItem.description.trim(),
        amount: finalAmount,
        type: newPnlItem.type
      });
      const createdItem = extractPnlItem(unwrapApiPayload(response));
      setPnlItems(prev => [...prev, createdItem]);
      setNewPnlItem({ description: "", amount: "", type: "revenue" });
      await refreshPnlSummary(pnlCaseId);
    } catch (error) {
      setPnlError(error?.message || "Unable to add P&L item.");
    } finally {
      setIsPnlSaving(false);
    }
  };

  const handleSavePnlItem = async () => {
    if (!editingPnlItem || !pnlCaseId) return;
    const finalAmount = normalizePnlAmount(editingPnlItem.amount, editingPnlItem.type);
    if (finalAmount === null) return;

    setIsPnlSaving(true);
    setPnlError("");
    try {
      const response = await updatePnlItem({
        itemId: editingPnlItem.id,
        description: editingPnlItem.description,
        amount: finalAmount,
        type: editingPnlItem.type
      });
      const updatedItem = extractPnlItem(unwrapApiPayload(response));
      setPnlItems(prev => prev.map((item) => (item.id === editingPnlItem.id ? { ...item, ...updatedItem } : item)));
      setEditingPnlItem(null);
      await refreshPnlSummary(pnlCaseId);
    } catch (error) {
      setPnlError(error?.message || "Unable to update P&L item.");
    } finally {
      setIsPnlSaving(false);
    }
  };

  const handleDeletePnlItem = async (itemId) => {
    if (!itemId || !pnlCaseId) return;
    setIsPnlSaving(true);
    setPnlError("");
    try {
      await deletePnlItem({ itemId });
      setPnlItems(prev => prev.filter((item) => item.id !== itemId));
      await refreshPnlSummary(pnlCaseId);
    } catch (error) {
      setPnlError(error?.message || "Unable to delete P&L item.");
    } finally {
      setIsPnlSaving(false);
    }
  };

  useEffect(() => {
    if (!pnlCaseId || activeTab !== "pnl") return;
    let isActive = true;

    const loadPnl = async () => {
      setIsPnlLoading(true);
      setPnlError("");
      try {
        const itemsResponse = await fetchPnlItems(pnlCaseId);
        if (isActive) {
          const itemsPayload = unwrapApiPayload(itemsResponse);
          setPnlItems(extractPnlItems(itemsPayload));
        }
      } catch (error) {
        if (isActive) {
          setPnlItems([]);
          setPnlError(error?.message || "Unable to load P&L items.");
        }
      }
      if (isActive) {
        await refreshPnlSummary(pnlCaseId, isActive);
        setIsPnlLoading(false);
      }
    };

    loadPnl();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnlCaseId, activeTab]);

  const computedRevenue = pnlItems
    .filter((item) => item.type === "revenue")
    .reduce((acc, item) => acc + item.amount, 0);
  const computedExpenses = pnlItems
    .filter((item) => item.type === "expense")
    .reduce((acc, item) => acc + item.amount, 0);
  const totalRevenue = pnlSummary?.totalRevenue ?? computedRevenue;
  const totalExpenses = pnlSummary ? -Math.abs(pnlSummary.totalExpenses) : computedExpenses;
  const netPnl = pnlSummary?.net ?? (totalRevenue + totalExpenses);

  const caseKey = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
  const aiReportList = Array.isArray(aiReports)
    ? aiReports
    : (caseKey ? (aiReports?.[caseKey] || []) : []);



  useEffect(() => {
    if (selectedCase?.id) {
      setActiveTab("information");
    }
  }, [selectedCase?.id]);

  // DocuSign state
  const [isDocuSignConnected, setIsDocuSignConnected] = useState(false);
  const [isConnectingDocuSign, setIsConnectingDocuSign] = useState(false);
  const [docuSignAccountInfo, setDocuSignAccountInfo] = useState(null);
  const [showDocuSignView, setShowDocuSignView] = useState(false);
  const [docuSignEmbedUrl, setDocuSignEmbedUrl] = useState(null);

  // HelloSign state
  const [isHelloSignConnected, setIsHelloSignConnected] = useState(false);
  const [isConnectingHelloSign, setIsConnectingHelloSign] = useState(false);
  const [helloSignAccountInfo, setHelloSignAccountInfo] = useState(null);
  const [showHelloSignView, setShowHelloSignView] = useState(false);
  const [helloSignEmbedUrl, setHelloSignEmbedUrl] = useState(null);

  // Collaboration state
  const [collaboratorEmail, setCollaboratorEmail] = useState("");
  const [collaborators, setCollaborators] = useState([]);
  const [isInviting, setIsInviting] = useState(false);
  const [isCollaborationLoading, setIsCollaborationLoading] = useState(false);
  const [collaborationError, setCollaborationError] = useState("");
  const [collaborationPlan, setCollaborationPlan] = useState(null);
  const [collaborationUsage, setCollaborationUsage] = useState(null);
  const [collaborationUpgrade, setCollaborationUpgrade] = useState(null);
  const [removingCollaboratorId, setRemovingCollaboratorId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [isTeamLoading, setIsTeamLoading] = useState(false);
  const isTeamLoadingRef = useRef(false); // Use ref for loading guard to avoid useCallback dependency issues
  const lastTeamAccessCaseRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadTeamAccess = useCallback(async (caseId, { force = false } = {}) => {
    if (!caseId) return;
    if (!force && lastTeamAccessCaseRef.current === caseId) return;
    // Use ref for loading guard to avoid recreating this callback
    if (isTeamLoadingRef.current) return;

    isTeamLoadingRef.current = true;
    setIsTeamLoading(true);
    try {
      // Fetch team members
      const teamRes = await fetchTeamMembers();
      const teamData = teamRes?.data?.data || teamRes?.data || teamRes || {};
      const teamList = Array.isArray(teamData.members) ? teamData.members : (Array.isArray(teamData) ? teamData : []);
      setTeamMembers(teamList);

      // Try to fetch assigned members, but don't fail if endpoint doesn't exist
      try {
        const accessRes = await fetchCaseTeamAccess(caseId);
        const accessData = accessRes?.data?.data || accessRes?.data || accessRes || {};
        const assignedList = Array.isArray(accessData.assignedMembers) ? accessData.assignedMembers : (Array.isArray(accessData) ? accessData : []);
        setAssignedMembers(assignedList);
      } catch {
        setAssignedMembers([]);
      }

      lastTeamAccessCaseRef.current = caseId;
    } catch (err) {
      console.error("Failed to load team data", err);
      setTeamMembers([]);
      setAssignedMembers([]);
    } finally {
      isTeamLoadingRef.current = false;
      setIsTeamLoading(false);
    }
  }, []); // Empty dependency array - refs don't need to be dependencies

  const resolveFileLink = (file) => {
    if (file?.objectKey) return "__objectKey__";
    return file?.signedUrl || file?.downloadUrl || file?.previewUrl || null;
  };

  const formatLoanAmount = (value) => {
    if (value === null || value === undefined) return "";
    const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    if (Number.isNaN(numeric)) return `$${String(value).replace(/^\$/, "")}`;
    return `$${numeric.toLocaleString()}`;
  };



  // Mock DocuSign OAuth Connection
  const handleConnectDocuSign = async () => {
    setIsConnectingDocuSign(true);

    // Simulate OAuth flow (In production: redirect to DocuSign OAuth)
    setTimeout(() => {
      // Mock successful OAuth response
      const mockAccountInfo = {
        accountId: `acc-${Date.now()}`,
        accountName: "Lender Demo Account",
        userName: "Demo Lender",
        email: selectedCase.email || "lender@example.com",
        baseUri: "https://demo.docusign.net",
        connected: true,
        connectedAt: new Date().toISOString()
      };

      setDocuSignAccountInfo(mockAccountInfo);
      setIsDocuSignConnected(true);
      setIsConnectingDocuSign(false);

      console.log("Mock DocuSign OAuth Success:", mockAccountInfo);
    }, 2000);
  };

  // Mock: Launch Embedded DocuSign Console
  const handleLaunchDocuSignConsole = () => {
    // In production: Call backend to get embedded console URL
    const mockEmbedUrl = `https://demo.docusign.net/console?embed=true&account=${docuSignAccountInfo?.accountId}&session=${Date.now()}`;

    setDocuSignEmbedUrl(mockEmbedUrl);
    setShowDocuSignView(true);

    console.log("Mock DocuSign Embedded Console URL:", mockEmbedUrl);
  };

  // Disconnect DocuSign
  const handleDisconnectDocuSign = () => {
    setIsDocuSignConnected(false);
    setDocuSignAccountInfo(null);
    setDocuSignEmbedUrl(null);
    setShowDocuSignView(false);
  };

  // Close embedded view
  const handleCloseDocuSignView = () => {
    setShowDocuSignView(false);
    setDocuSignEmbedUrl(null);
  };

  // Mock HelloSign OAuth Connection
  const handleConnectHelloSign = async () => {
    setIsConnectingHelloSign(true);

    // Simulate OAuth flow
    setTimeout(() => {
      const mockAccountInfo = {
        accountId: `acc-hs-${Date.now()}`,
        accountName: "Lender Demo Account (HelloSign)",
        userName: "Demo Lender",
        email: selectedCase.email || "lender@example.com",
        connected: true,
        connectedAt: new Date().toISOString()
      };

      setHelloSignAccountInfo(mockAccountInfo);
      setIsHelloSignConnected(true);
      setIsConnectingHelloSign(false);

      console.log("Mock HelloSign OAuth Success:", mockAccountInfo);
    }, 2000);
  };

  // Mock: Launch Embedded HelloSign Console
  const handleLaunchHelloSignConsole = () => {
    const mockEmbedUrl = `https://app.hellosign.com/editor/embedded?mock=true&session=${Date.now()}`;

    setHelloSignEmbedUrl(mockEmbedUrl);
    setShowHelloSignView(true);

    console.log("Mock HelloSign Embedded Console URL:", mockEmbedUrl);
  };

  // Disconnect HelloSign
  const handleDisconnectHelloSign = () => {
    setIsHelloSignConnected(false);
    setHelloSignAccountInfo(null);
    setHelloSignEmbedUrl(null);
    setShowHelloSignView(false);
  };

  // Close embedded view
  const handleCloseHelloSignView = () => {
    setShowHelloSignView(false);
    setHelloSignEmbedUrl(null);
  };

  const applyCollaborationPayload = (payload = {}) => {
    const plan = payload?.plan || null;
    const usage = payload?.usage || null;
    const list = Array.isArray(payload?.collaborators) ? payload.collaborators : [];
    setCollaborationPlan(plan);
    setCollaborationUsage(usage);
    setCollaborators(list);
  };

  useEffect(() => {
    if (!caseKey || activeTab !== "collaboration") return;

    let isActive = true;
    setIsCollaborationLoading(true);
    setCollaborationError("");
    setCollaborationUpgrade(null);

    fetchCaseCollaboration(caseKey)
      .then((response) => {
        if (!isActive) return;
        const payload = response?.data?.data || response?.data || response || {};
        applyCollaborationPayload(payload);
      })
      .catch((error) => {
        if (!isActive) return;
        setCollaborators([]);
        setCollaborationPlan(null);
        setCollaborationUsage(null);
        setCollaborationError(error?.message || "Unable to load collaboration details.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsCollaborationLoading(false);
      });

    loadTeamAccess(caseKey, { force: true });

    return () => {
      isActive = false;
    };
  }, [caseKey, activeTab, loadTeamAccess]);

  useEffect(() => {
    if (!caseKey) {
      setTeamMembers([]);
      setAssignedMembers([]);
      lastTeamAccessCaseRef.current = null;
      return;
    }
    loadTeamAccess(caseKey);
  }, [caseKey, loadTeamAccess]);

  const handleInviteCollaborator = async () => {
    if (!caseKey || !collaboratorEmail.trim()) return;
    setIsInviting(true);
    setCollaborationError("");
    setCollaborationUpgrade(null);
    try {
      const response = await createCaseCollaboratorInvite({
        caseId: caseKey,
        email: collaboratorEmail.trim(),
        role: "collaborator"
      });
      const payload = response?.data?.data || response?.data || response || {};
      const nextInvite = {
        email: payload?.email || collaboratorEmail.trim(),
        status: payload?.status || "Pending",
        invitedAt: payload?.invitedAt || new Date().toISOString()
      };
      setCollaborators((prev) => [...prev, nextInvite]);
      if (payload?.usage) {
        setCollaborationUsage(payload.usage);
      }
      setCollaboratorEmail("");
    } catch (error) {
      if (error?.status === 409) {
        const data = error?.data || {};
        if (data?.plan) setCollaborationPlan(data.plan);
        if (data?.usage) setCollaborationUsage(data.usage);
        if (data?.upgrade) setCollaborationUpgrade(data.upgrade);
        setCollaborationError(data?.message || "Invite limit reached for your plan.");
      } else {
        setCollaborationError(error?.message || "Unable to send invite.");
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveCollaborator = async (collaborator) => {
    if (!caseKey) return;
    const collaboratorId =
      collaborator?.id || collaborator?.inviteId || collaborator?.collaboratorId || collaborator?.email;
    if (!collaboratorId) return;
    setRemovingCollaboratorId(collaboratorId);
    setCollaborationError("");
    try {
      await revokeCaseCollaborator({ caseId: caseKey, collaboratorId });
      setCollaborators((prev) =>
        prev.filter((item) => {
          const itemId = item?.id || item?.inviteId || item?.collaboratorId || item?.email;
          return itemId !== collaboratorId;
        })
      );
    } catch (error) {
      setCollaborationError(error?.message || "Unable to remove collaborator.");
    } finally {
      setRemovingCollaboratorId(null);
    }
  };

  // Acceleration handlers
  const handleAccelerateClick = (option) => {
    setAccelerationOption(option);
    setIsAccelerationModalOpen(true);
  };

  const handleAccelerationConfirm = async () => {
    if (!accelerationOption || !caseKey) return;

    // Get the latest run ID from pipeline status
    const runId = aiPipelineStatus?.run_id || pipelineRuns[0]?.run_id;
    if (!runId) {
      alert('No active report delivery found');
      return;
    }

    setIsAccelerating(true);
    try {
      const result = await accelerateDelivery(caseKey, runId, accelerationOption.level);

      // Update delivery time
      setDeliveryTime(result.new_delivery_time);

      // Refresh credits
      if (onRefreshCredits) {
        onRefreshCredits();
      }

      // Show success message
      alert(`✅ Delivery accelerated! You'll receive your report in ${result.time_saved_hours} fewer hours. Credits remaining: ${result.credits_remaining}`);

      // Close modal
      setIsAccelerationModalOpen(false);
      setAccelerationOption(null);
    } catch (error) {
      const errorMessage = error.message || 'Failed to accelerate delivery';

      if (errorMessage.includes('Insufficient credits')) {
        alert('❌ ' + errorMessage + '\n\nWould you like to purchase more credits?');
        // Optionally redirect to pricing
      } else if (errorMessage.includes('already been delivered')) {
        alert('ℹ️ This report has already been sent to your email.');
      } else if (errorMessage.includes('not found')) {
        alert('❌ Delivery not found. Please try again.');
      } else {
        alert('❌ ' + errorMessage);
      }
    } finally {
      setIsAccelerating(false);
    }
  };

  // Calculate delivery time when pipeline status changes
  useEffect(() => {
    if (aiPipelineStatus?.status === 'SUCCEEDED' && !deliveryTime) {
      const calculatedTime = calculateDeliveryTime(userTier, new Date(aiPipelineStatus.started_at));
      setDeliveryTime(calculatedTime.toISOString());
    }
  }, [aiPipelineStatus, userTier, deliveryTime]);

  const remainingInvites = collaborationUsage?.remainingInvites;
  const inviteLimit = collaborationPlan?.inviteLimitPerCase;
  const invitesSent = collaborationUsage?.invitesSent;
  const atInviteLimit = remainingInvites === 0;
  const canInvite = remainingInvites === undefined || remainingInvites === null ? true : remainingInvites > 0;
  const usagePercent = inviteLimit ? Math.min(100, Math.round(((invitesSent || 0) / inviteLimit) * 100)) : 0;
  const suggestedTier =
    collaborationUpgrade?.suggestedTier || (collaborationPlan?.tier === "Free" ? "Starter" : "Pro");
  const suggestedLimit =
    collaborationUpgrade?.inviteLimitPerCase || (collaborationPlan?.tier === "Free" ? 3 : 10);
  const handleUpgradeClick = () => {
    if (typeof onUpgradePlan === "function") {
      onUpgradePlan();
    }
  };

  if (!selectedCase) return null;

  const reserveRightPanel = modalSize === "full" && isAccountSettingsOpen;
  const modalSizeClass = (() => {
    switch (modalSize) {
      case "wide":
        return "max-w-4xl max-h-[90vh]";
      case "full":
        return reserveRightPanel
          ? "max-w-[95vw] max-h-[95vh] md:max-w-[calc(100vw-18rem-36rem)]"
          : "max-w-[95vw] max-h-[95vh] md:max-w-[calc(100vw-18rem)]";
      default:
        return "max-w-2xl max-h-[85vh]";
    }
  })();

  // Filter out failed runs - only show successful and in-progress runs
  const filteredRuns = Array.isArray(pipelineRuns)
    ? pipelineRuns.filter(run => {
        const status = (run?.status || "").toUpperCase();
        return status !== "FAILED";
      })
    : [];
  const runHistory = filteredRuns.length > 0
    ? filteredRuns
    : (aiPipelineStatus && (aiPipelineStatus.status || "").toUpperCase() !== "FAILED" ? [aiPipelineStatus] : []);
  const activeRun = runHistory.find((run) => {
    const status = (run?.status || "").toUpperCase();
    return ["RUNNING", "PENDING"].includes(status) && !run?.completed_at;
  });
  const currentPipeline = activeRun || aiPipelineStatus || null;
  const pipelineStatusValue = (currentPipeline?.status || "").toUpperCase();
  const isPipelineActive = ["RUNNING", "PENDING"].includes(pipelineStatusValue);
  const isExplicitlyStalled = ["RUNNING_STALLED", "STALLED"].includes(pipelineStatusValue);
  // unexpected long running job > 1 hour
  const isStalled = isExplicitlyStalled || (isPipelineActive && currentPipeline?.started_at && (Date.now() - new Date(currentPipeline.started_at).getTime() > 60 * 60 * 1000));
  const formatPipelineStatus = (status) => {
    if (!status) return "Unknown";
    return status
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const toDisplayString = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (value?.message) return value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  };
  const formatRunTimestamp = (timestamp) => {
    if (!timestamp) return "Time unavailable";
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? "Time unavailable" : date.toLocaleString();
  };
  const resolveReportName = (report, index) => {
    const candidates = [
      report?.name,
      report?.report_name,
      report?.title,
      report?.filename,
      report?.file_name,
      report?.run_id,
      report?.id
    ];
    for (const candidate of candidates) {
      const text = toDisplayString(candidate);
      if (text) return text;
    }
    return `AI Report ${index + 1}`;
  };
  const resolveRunLabel = (run, index) => {
    const runId = run?.run_id || "";
    const runIdMatch = runId.match(/^run-(\d{4}-\d{2}-\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
    if (runIdMatch) {
      const [, datePart, hour, minute, second] = runIdMatch;
      return `Run ${datePart} ${hour}:${minute}:${second}`;
    }
    const candidates = [
      run?.name,
      run?.run_name,
      run?.pipeline_name,
      run?.display_name,
      runId
    ];
    for (const candidate of candidates) {
      const text = toDisplayString(candidate);
      if (text) return text;
    }
    return `Run ${index + 1}`;
  };
  const resolveRunStatus = (run) => {
    const status = (run?.status || "").toUpperCase();
    const hasCompleted = !!(run?.completed_at || run?.completedAt || run?.ended_at || run?.endedAt || run?.finished_at || run?.finishedAt);
    const hasError = !!(run?.error || run?.error_message);

    // Debug logging for stuck runs
    if (status === "RUNNING" && run?.run_id) {
      console.log(`[DEBUG] Run showing as RUNNING:`, {
        run_id: run.run_id,
        status: run.status,
        completed_at: run.completed_at,
        completedAt: run.completedAt,
        ended_at: run.ended_at,
        endedAt: run.endedAt,
        started_at: run.started_at,
        hasCompleted,
        fullRun: run
      });
    }

    // If status is explicitly set to SUCCEEDED or FAILED, trust it
    if (status === "SUCCEEDED" || status === "FAILED") {
      return status;
    }

    // Check completion fields
    if (hasCompleted) {
      return hasError ? "FAILED" : "SUCCEEDED";
    }

    // If status exists and is not RUNNING/PENDING, return it
    if (status && !["RUNNING", "PENDING", ""].includes(status)) {
      return status;
    }

    // Check for error without completion
    if (hasError) return "FAILED";

    // If we have a status field, return it
    if (status) return status;

    // Default to UNKNOWN
    return "UNKNOWN";
  };
  const formatPipelineError = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?.message) return value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return "Unknown error";
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center dark:bg-slate-900/60 bg-slate-500/50 backdrop-blur-sm px-4 py-8 overflow-y-auto ${modalSize === "full"
          ? `md:justify-start md:pl-64 ${reserveRightPanel ? "md:pr-[36rem]" : "md:pr-4"}`
          : ""}`}
        onClick={onClose}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
        }}
        tabIndex={-1}
      >
        <div
          className={`dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full shadow-2xl overflow-y-auto ${modalSizeClass}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 dark:bg-slate-900 bg-white">
            <div className="flex items-start justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Case Details</p>
                <h3 className="text-2xl font-bold dark:text-white text-slate-900">{selectedCase.name}</h3>
                <p className="dark:text-slate-400 text-slate-500">{selectedCase.loanType} • {selectedCase.status}</p>
                <p className="text-slate-500 text-sm mt-1">Case #{selectedCase.caseNumber}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 rounded-full border dark:border-slate-800 border-slate-200 dark:bg-slate-950/60 bg-slate-100 px-2 py-1 text-xs">
                  <span className="text-slate-500">Size</span>
                  {[
                    { id: "default", label: "Default" },
                    { id: "wide", label: "Wide" },
                    { id: "full", label: "Full" }
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setModalSize(option.id)}
                      className={`px-2 py-0.5 rounded-full transition-colors ${modalSize === option.id
                        ? "bg-yellow-500/20 text-yellow-500 dark:text-yellow-300"
                        : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                      aria-pressed={modalSize === option.id}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="border-b dark:border-slate-800 border-slate-200 px-6">
              <div className="flex gap-6">
                <button
                  onClick={() => setActiveTab("information")}
                  className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative ${activeTab === "information"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                  Information
                  {activeTab === "information" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                  )}
                </button>

                <button
                  onClick={() => setActiveTab("ai_analysis")}
                  className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative ${activeTab === "ai_analysis"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                  AI Report
                  {activeTab === "ai_analysis" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                  )}
                </button>
                {/*               <button
                  onClick={() => setActiveTab("doc-signing")}
                  className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative ${activeTab === "doc-signing"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                  Doc Signing
                  {activeTab === "doc-signing" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                  )}
                </button> */}
                <button
                  onClick={() => setActiveTab("tasks")}
                  className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative flex items-center gap-2 ${activeTab === "tasks"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                  <ListTodo className="w-4 h-4" />
                  Tasks
                  {activeTab === "tasks" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                  )}
                </button>
                {featureFlags.hasCollaboration && (
                  <button
                    onClick={() => setActiveTab("collaboration")}
                    className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative ${activeTab === "collaboration"
                      ? "text-yellow-600 dark:text-yellow-400"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                      }`}
                  >
                    Team Access
                    {activeTab === "collaboration" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("pnl")}
                  className={`pb-4 pt-2 px-2 font-semibold text-sm transition-colors relative ${activeTab === "pnl"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                  P&L
                  {activeTab === "pnl" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {isCaseLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
                <span className="ml-3 text-slate-400 text-sm">Loading case data...</span>
              </div>
            )}
            {!isCaseLoading && (
              <>
                {/* Tab Content: Information */}
                {activeTab === "information" && (
                  <>
                    {/* Edit Controls */}
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold dark:text-slate-300 text-slate-700">Case Details</h4>
                      <div className="flex items-center gap-2">
                        {updateError && (
                          <span className="text-red-500 text-sm mr-2">{updateError}</span>
                        )}
                        {isEditMode ? (
                          <>
                            <button
                              onClick={handleEditToggle}
                              disabled={isUpdating}
                              className="px-3 py-1.5 text-sm dark:bg-slate-700 bg-slate-200 dark:text-slate-300 text-slate-700 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleCaseUpdate}
                              disabled={isUpdating}
                              className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {isUpdating ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="w-3.5 h-3.5" />
                                  Save Changes
                                </>
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleEditToggle}
                            className="px-3 py-1.5 text-sm dark:bg-slate-700 bg-slate-200 dark:text-slate-300 text-slate-700 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors flex items-center gap-1.5"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Borrower Name */}
                      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Borrower Name</p>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={editedCase.name}
                            onChange={(e) => setEditedCase({ ...editedCase, name: e.target.value })}
                            className="w-full mt-2 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        ) : (
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">{selectedCase.name || "Not provided"}</p>
                        )}
                      </div>

                      {/* Property Address */}
                      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Property Address</p>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={editedCase.propertyAddress}
                            onChange={(e) => setEditedCase({ ...editedCase, propertyAddress: e.target.value })}
                            className="w-full mt-2 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        ) : (
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">{selectedCase.propertyAddress || "Not provided"}</p>
                        )}
                      </div>

                      {/* Loan Type */}
                      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Loan Type</p>
                        {isEditMode ? (
                          <select
                            value={editedCase.loanType}
                            onChange={(e) => setEditedCase({ ...editedCase, loanType: e.target.value })}
                            className="w-full mt-2 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          >
                            <option value="">Select loan type</option>
                            <option value="Conventional">Conventional</option>
                            <option value="FHA">FHA</option>
                            <option value="VA">VA</option>
                            <option value="USDA">USDA</option>
                            <option value="Jumbo">Jumbo</option>
                            <option value="Non-QM">Non-QM</option>
                            <option value="HELOC">HELOC</option>
                            <option value="Reverse">Reverse</option>
                          </select>
                        ) : (
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">{selectedCase.loanType || "Not provided"}</p>
                        )}
                      </div>

                      {/* Loan Amount */}
                      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Loan Amount</p>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={editedCase.loanAmount}
                            onChange={(e) => setEditedCase({ ...editedCase, loanAmount: e.target.value })}
                            placeholder="e.g., 500000"
                            className="w-full mt-2 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        ) : (
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">
                            {selectedCase.loanAmount ? formatLoanAmount(selectedCase.loanAmount) : "Pending"}
                          </p>
                        )}
                      </div>

                      {/* Borrower Email */}
                      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Borrower Email</p>
                        {isEditMode ? (
                          <input
                            type="email"
                            value={editedCase.email}
                            onChange={(e) => setEditedCase({ ...editedCase, email: e.target.value })}
                            className="w-full mt-2 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        ) : (
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">{selectedCase.email || "Not provided"}</p>
                        )}
                      </div>
                    </div>

                    <div className="border dark:border-slate-800 border-slate-200 rounded-xl p-5 dark:bg-slate-900/70 bg-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Case Status</p>
                          <p className="dark:text-white text-slate-900 font-semibold mt-2">{selectedCase.status}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isStatusUpdating && <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />}
                          <select
                            value={selectedCase.status}
                            onChange={onStatusChange}
                            className="dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none text-sm disabled:opacity-60"
                            disabled={isStatusUpdating}
                          >
                            {["Processing", "Underwriting", "Action Required", "Approved", "Cleared to Close", "Closing", "Funded", "Fall out"].map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <DocumentsWidget
                      caseId={selectedCase.id}
                      caseFiles={caseFiles}
                      isUploading={isUploading}
                      uploadMessage={uploadMessage}
                      uploadProgress={uploadProgress}
                      isCaseHydrating={isCaseHydrating}
                      onDocumentUpload={onDocumentUpload}
                      handlePreview={handlePreview}
                      onDeleteFile={onDeleteFile}
                      isDeletingFile={isDeletingFile}
                      fileDeleteTarget={fileDeleteTarget}
                    />




                    {/* Comments Section - in Information Tab */}
                    <CommentsWidget
                      comments={caseComments}
                      onAddComment={onAddComment}
                      isCommenting={isCommenting}
                    />
                  </>
                )}

                {activeTab === "forms" && (
                  <FormsTab
                    formsCatalog={formsCatalog}
                    existingFormNames={existingFormNames}
                    isAddingFormId={isAddingFormId}
                    onPreview={handlePreview}
                    onAddForm={handleAddForm}
                  />
                )}

                {/* Tab Content: AI Analysis */}
                {activeTab === "ai_analysis" && (
                  <div className="space-y-5">
                    <div className="border dark:border-slate-800 border-slate-200 rounded-xl p-5 dark:bg-slate-900/70 bg-white">
                      <div className="mb-3">
                        <div>
                          <p className="text-sm font-semibold dark:text-white text-slate-900">AI Underwriting Report</p>
                          <p className="text-xs text-slate-500">
                            Choose Standard (150 credits) or Deep Reasoning Audit (1,000 credits) based on file complexity.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={onGenerateAi}
                          disabled={isAiGenerating}
                          className="px-3 py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors disabled:opacity-60 flex items-center gap-2 text-sm"
                        >
                          {isAiGenerating && <Loader2 className="w-4 h-4 animate-spin" />}
                          {isAiGenerating
                            ? "Requesting..."
                            : pipelineStatusValue === "FAILED"
                              ? "Retry AI Report"
                              : "Generate AI Report"}
                        </button>
                      </div>
                      {/* Unified AI Activity Feed */}
                      <div className="space-y-3 mt-3">
                        {/* Messages */}
                        {caseError && (
                          <p className="text-red-400 text-sm bg-red-900/20 p-2 rounded border border-red-900/50">
                            {typeof caseError === "string" ? caseError : caseError?.message || "Error status unavailable."}
                          </p>
                        )}
                        {aiMessage && <p className="text-yellow-400 text-sm">{aiMessage}</p>}

                        {/* Combined List */}
                        {(() => {
                          const mergedItems = [
                            ...(runHistory || []).map(r => ({ ...r, _type: 'run', _date: new Date(r.started_at || 0) })),
                            ...(aiReportList || []).map(r => ({ ...r, _type: 'report', _date: new Date(r.startedAt || r.started_at || Date.now()) }))
                          ].sort((a, b) => b._date - a._date);

                          if (mergedItems.length === 0) return <p className="text-slate-500 text-sm">No AI activity recorded.</p>;

                          return (
                            <div className="p-3 dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-lg text-xs space-y-2">
                              <p className="text-[10px] uppercase text-slate-500 font-bold">Activity Log</p>
                              <div className="space-y-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                {mergedItems.map((item, index) => {
                                  if (item._type === 'report') {
                                    // Report Item
                                    const status = item.status || "PENDING";
                                    const isReady = status === "SUCCEEDED";
                                    return (
                                      <div key={`report-${item.id}-${index}`} className="flex items-center gap-3 justify-between text-sm dark:text-slate-200 text-slate-800 dark:bg-slate-900 bg-white rounded-lg px-3 py-2 border dark:border-slate-800 border-slate-200 shadow-sm">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-0.5">
                                            <p className="truncate font-semibold">{resolveReportName(item, index)}</p>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${status === "SUCCEEDED" ? "bg-green-500/10 text-green-400" :
                                              status === "FAILED" ? "bg-red-500/10 text-red-400" :
                                                "bg-yellow-500/10 text-yellow-400"
                                              }`}>
                                              {formatPipelineStatus(status)}
                                            </span>
                                          </div>
                                          <p className="text-xs text-slate-500">
                                            {isReady ? "Ready for review" : `ETA: ${toDisplayString(item.eta, "Calculating...")}`}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {isReady && (
                                            <>
                                              <button
                                                onClick={() => handleViewAiReport(item, { download: false })}
                                                disabled={isFetchingReport === item.id}
                                                className={`px-3 py-1 rounded-lg border border-yellow-500/40 text-yellow-300 hover:text-yellow-200 hover:border-yellow-400 transition-colors text-xs font-semibold ${isFetchingReport === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                              >
                                                {isFetchingReport === item.id ? "Loading..." : "Preview"}
                                              </button>
                                              <button
                                                onClick={() => handleViewAiReport(item, { download: true })}
                                                disabled={isFetchingReport === item.id}
                                                className={`px-3 py-1 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors text-xs ${isFetchingReport === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                              >
                                                Download
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    // Run Item
                                    const runStatus = resolveRunStatus(item);
                                    const runLabel = resolveRunLabel(item, index);
                                    return (
                                      <div key={`run-${item.run_id}-${index}`} className="flex items-center gap-3 justify-between text-sm dark:text-slate-200 text-slate-800 dark:bg-slate-900 bg-white rounded-lg px-3 py-2 border dark:border-slate-800 border-slate-200 opacity-80 hover:opacity-100 transition-opacity shadow-sm">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-0.5">
                                            <p className="truncate font-semibold dark:text-slate-200 text-slate-800">{runLabel}</p>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${runStatus === "RUNNING" ? "bg-yellow-500/10 text-yellow-400" :
                                              runStatus === "SUCCEEDED" ? "bg-green-500/10 text-green-400" :
                                                runStatus === "FAILED" ? "bg-red-500/10 text-red-400" :
                                                  "bg-slate-500/10 text-slate-400"
                                              }`}>
                                              {formatPipelineStatus(runStatus)}
                                            </span>
                                            {runStatus === "FAILED" && (item.error_message || item.error) && (
                                              <span className="text-[10px] text-red-400 font-bold px-1 bg-red-900/30 rounded">Error</span>
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-500">{formatRunTimestamp(item.started_at)}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {runStatus === "SUCCEEDED" && (
                                            <>
                                              <button
                                                onClick={() => handleViewAiReport(item, { download: false })}
                                                disabled={isFetchingReport === item.run_id}
                                                className={`px-3 py-1 rounded-lg border border-yellow-500/40 text-yellow-300 hover:text-yellow-200 hover:border-yellow-400 transition-colors text-xs font-semibold ${isFetchingReport === item.run_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                              >
                                                {isFetchingReport === item.run_id ? "Loading..." : "Preview"}
                                              </button>
                                              <button
                                                onClick={() => handleViewAiReport(item, { download: true })}
                                                disabled={isFetchingReport === item.run_id}
                                                className={`px-3 py-1 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors text-xs ${isFetchingReport === item.run_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                              >
                                                Download
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            </div>
                          );
                        })()}

                      </div>
                    </div>


                    {/* Acceleration Card - MOVED TO PRE-GENERATION MODAL */}
                    {/* Acceleration is now selected BEFORE generating the report, not after */}
                    {false && deliveryTime && aiPipelineStatus?.status === 'SUCCEEDED' && (
                      <AccelerationCard
                        deliveryTime={deliveryTime}
                        options={getAccelerationOptions(userTier, userCredits)}
                        onAccelerate={handleAccelerateClick}
                        loading={isAccelerating}
                      />
                    )}
                  </div>
                )}

                {/* Tab Content: Doc Signing */}
                {activeTab === "doc-signing" && (
                  <div className="space-y-5">
                    {/* DocuSign Account Integration */}
                    <div className="border border-yellow-800/50 rounded-xl p-6 bg-gradient-to-br from-yellow-950/30 to-slate-900/70">
                      <div className="mb-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-yellow-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">DocuSign Integration</h3>
                            <p className="text-sm text-slate-400">
                              Access your DocuSign account without leaving the app
                            </p>
                          </div>
                        </div>
                      </div>

                      {(!isDocuSignConnected && !isHelloSignConnected) && (
                        <div className="space-y-6">
                          {/* Not Connected State */}
                          <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-6 space-y-4">
                            <h4 className="text-lg font-semibold dark:text-white text-slate-900">Connect Your DocuSign Account</h4>
                            <p className="text-slate-400 text-sm leading-relaxed">
                              Connect your DocuSign account to manage documents, send envelopes, and track signatures - all without leaving this application.
                            </p>

                            <div className="bg-yellow-950/30 border border-yellow-800/50 rounded-lg p-4">
                              <p className="text-sm font-semibold text-yellow-400 mb-2">What you can do:</p>
                              <ul className="space-y-2 text-sm text-slate-300">
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Create and send envelopes for signature</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Track document status in real-time</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Manage templates and recipients</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Access your DocuSign dashboard embedded in the app</span>
                                </li>
                              </ul>
                            </div>

                            <button
                              onClick={handleConnectDocuSign}
                              disabled={isConnectingDocuSign}
                              className="w-full px-6 py-4 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-base"
                            >
                              {isConnectingDocuSign ? (
                                <>
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                  Connecting to DocuSign...
                                </>
                              ) : (
                                <>
                                  <FileText className="w-5 h-5" />
                                  Connect to DocuSign
                                </>
                              )}
                            </button>

                            <p className="text-xs text-slate-500 text-center">
                              You'll be securely authenticated with DocuSign OAuth
                            </p>
                          </div>
                          <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-6 space-y-4">
                            <h4 className="text-lg font-semibold dark:text-white text-slate-900">Connect Your HelloSign Account</h4>
                            <p className="text-slate-400 text-sm leading-relaxed">
                              Connect your HelloSign account to manage documents, send envelopes, and track signatures - all without leaving this application.
                            </p>

                            <div className="bg-yellow-950/30 border border-yellow-800/50 rounded-lg p-4">
                              <p className="text-sm font-semibold text-yellow-400 mb-2">What you can do:</p>
                              <ul className="space-y-2 text-sm text-slate-300">
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Create and send signature requests</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Track document status in real-time</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Manage templates and recipients</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                  <span>Access your HelloSign dashboard embedded in the app</span>
                                </li>
                              </ul>
                            </div>

                            <button
                              onClick={handleConnectHelloSign}
                              disabled={isConnectingHelloSign}
                              className="w-full px-6 py-4 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-base"
                            >
                              {isConnectingHelloSign ? (
                                <>
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                  Connecting to HelloSign...
                                </>
                              ) : (
                                <>
                                  <FileText className="w-5 h-5" />
                                  Connect to HelloSign
                                </>
                              )}
                            </button>

                            <p className="text-xs text-slate-500 text-center">
                              You'll be securely authenticated with HelloSign OAuth
                            </p>
                          </div>
                        </div>
                      )}

                      {(isDocuSignConnected || isHelloSignConnected) && (
                        /* Connected State */
                        <div className="space-y-4">
                          {/* Account Info */}
                          {isDocuSignConnected && (
                            <div className="bg-green-950/20 border border-green-800/50 rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-green-400">Connected to DocuSign</p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      {docuSignAccountInfo?.accountName} • {docuSignAccountInfo?.email}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      Account ID: {docuSignAccountInfo?.accountId}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={handleDisconnectDocuSign}
                                  className="text-xs text-red-400 hover:text-red-300 underline"
                                >
                                  Disconnect
                                </button>
                              </div>
                            </div>
                          )}

                          {isHelloSignConnected && (
                            <div className="bg-green-950/20 border border-green-800/50 rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-green-400">Connected to HelloSign</p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      {helloSignAccountInfo?.accountName} • {helloSignAccountInfo?.email}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      Account ID: {helloSignAccountInfo?.accountId}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={handleDisconnectHelloSign}
                                  className="text-xs text-red-400 hover:text-red-300 underline"
                                >
                                  Disconnect
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Launch DocuSign Console */}
                          {isDocuSignConnected && (
                            <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-6 space-y-4">
                              <div>
                                <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">Access Your DocuSign Dashboard</h4>
                                <p className="text-sm text-slate-400">
                                  Open your DocuSign console in an embedded view. You can create envelopes, manage templates, and track signatures without leaving this app.
                                </p>
                              </div>

                              <button
                                onClick={handleLaunchDocuSignConsole}
                                className="w-full px-6 py-4 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors flex items-center justify-center gap-3 text-base"
                              >
                                <FileText className="w-5 h-5" />
                                Open DocuSign Console
                              </button>
                            </div>
                          )}

                          {/* Launch HelloSign Console */}
                          {isHelloSignConnected && (
                            <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-6 space-y-4">
                              <div>
                                <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">Access Your HelloSign Dashboard</h4>
                                <p className="text-sm text-slate-400">
                                  Open your HelloSign console in an embedded view. You can create envelopes, manage templates, and track signatures without leaving this app.
                                </p>
                              </div>

                              <button
                                onClick={handleLaunchHelloSignConsole}
                                className="w-full px-6 py-4 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors flex items-center justify-center gap-3 text-base"
                              >
                                <FileText className="w-5 h-5" />
                                Open HelloSign Console
                              </button>
                            </div>
                          )}

                          {/* Quick Actions */}
                          <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-4">
                            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Quick Actions</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <button
                                onClick={isDocuSignConnected ? handleLaunchDocuSignConsole : handleLaunchHelloSignConsole}
                                className="px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 transition-colors text-sm text-left"
                              >
                                <p className="font-semibold">Send New Envelope</p>
                                <p className="text-xs text-slate-500 mt-0.5">Create and send documents</p>
                              </button>
                              <button
                                onClick={isDocuSignConnected ? handleLaunchDocuSignConsole : handleLaunchHelloSignConsole}
                                className="px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 transition-colors text-sm text-left"
                              >
                                <p className="font-semibold">View Envelopes</p>
                                <p className="text-xs text-slate-500 mt-0.5">Track sent documents</p>
                              </button>
                              <button
                                onClick={isDocuSignConnected ? handleLaunchDocuSignConsole : handleLaunchHelloSignConsole}
                                className="px-4 py-3 rounded-lg dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 dark:text-slate-200 text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm text-left"
                              >
                                <p className="font-semibold">Manage Templates</p>
                                <p className="text-xs text-slate-500 mt-0.5">Use pre-configured forms</p>
                              </button>
                              <button
                                onClick={isDocuSignConnected ? handleLaunchDocuSignConsole : handleLaunchHelloSignConsole}
                                className="px-4 py-3 rounded-lg dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 dark:text-slate-200 text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm text-left"
                              >
                                <p className="font-semibold">Account Settings</p>
                                <p className="text-xs text-slate-500 mt-0.5">Manage your account</p>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab Content: Collaboration */}
                {activeTab === "collaboration" && (
                  <div className="space-y-5">
                    <div className="dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-4">
                      <h4 className="dark:text-white text-slate-900 font-semibold mb-2">Team Access</h4>
                      <p className="text-sm text-slate-400 mb-4">
                        Control which team members can view and edit this case.
                      </p>

                      {isTeamLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading team members...
                        </div>
                      ) : teamMembers.length === 0 ? (
                        <div className="text-sm text-slate-500">
                          No team members found. Go to Teams to add members.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {teamMembers.map(member => {
                            const isAssigned = assignedMembers.includes(member.id);
                            return (
                              <div key={member.id} className="flex items-center justify-between dark:bg-slate-900 bg-slate-50 p-3 rounded-lg border dark:border-slate-800 border-slate-200">
                                <div>
                                  <p className="text-sm font-medium dark:text-white text-slate-900">{member.name}</p>
                                  <p className="text-xs text-slate-500">{member.email}</p>
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      if (isAssigned) {
                                        await removeMemberFromCase(caseKey, member.id);
                                        setAssignedMembers(prev => prev.filter(id => id !== member.id));
                                      } else {
                                        await assignMemberToCase(caseKey, member.id);
                                        setAssignedMembers(prev => [...prev, member.id]);
                                      }
                                    } catch (e) {
                                      console.error("Failed to toggle assignment", e);
                                    }
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${isAssigned
                                    ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                    : "bg-green-500/10 text-green-400 hover:bg-green-500/20"
                                    }`}
                                >
                                  {isAssigned ? "Revoke Access" : "Grant Access"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Tab Content: P&L */}
                {activeTab === "pnl" && (
                  <div className="space-y-5">
                    <div className="border dark:border-slate-800 border-slate-200 rounded-xl p-5 dark:bg-slate-900/70 bg-white">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="dark:text-white text-slate-900 font-bold text-lg">Profit & Loss Statement</h3>
                        {isPnlSaving && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </div>
                        )}
                      </div>
                      {pnlError && (
                        <p className="text-sm text-red-400 mb-3">{pnlError}</p>
                      )}
                      {isPnlLoading && (
                        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading P&L items...
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="border-b border-t dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-slate-50 text-slate-500">
                            <tr>
                              <th className="p-4 font-medium">Description</th>
                              <th className="p-4 font-medium text-right">Amount</th>
                              <th className="p-4 font-medium">Type</th>
                              <th className="p-4 font-medium"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                            {pnlItems.map((item) => (
                              <tr key={item.id}>
                                {editingPnlItem?.id === item.id ? (
                                  <>
                                    <td className="p-2">
                                      <input
                                        type="text"
                                        value={editingPnlItem.description}
                                        onChange={(e) => setEditingPnlItem({ ...editingPnlItem, description: e.target.value })}
                                        className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                                      />
                                    </td>
                                    <td className="p-2">
                                      <input
                                        type="number"
                                        value={normalizePnlInputValue(editingPnlItem.amount)}
                                        onChange={(e) => setEditingPnlItem({ ...editingPnlItem, amount: e.target.value })}
                                        className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                                      />
                                    </td>
                                    <td className="p-2">
                                      <select
                                        value={editingPnlItem.type}
                                        onChange={(e) => setEditingPnlItem({ ...editingPnlItem, type: e.target.value })}
                                        className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                                      >
                                        <option value="revenue">Revenue</option>
                                        <option value="expense">Expense</option>
                                      </select>
                                    </td>
                                    <td className="p-2 text-right">
                                      <button
                                        className="text-green-400 hover:text-green-300 text-xs font-semibold disabled:opacity-60"
                                        onClick={handleSavePnlItem}
                                        disabled={isPnlSaving}
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="text-slate-400 hover:text-slate-300 text-xs font-semibold ml-4 disabled:opacity-60"
                                        onClick={() => setEditingPnlItem(null)}
                                        disabled={isPnlSaving}
                                      >
                                        Cancel
                                      </button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="p-4 font-medium dark:text-white text-slate-900">{item.description}</td>
                                    <td className={`p-4 text-slate-300 text-right ${item.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {item.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </td>
                                    <td className="p-4">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.type === 'revenue'
                                        ? 'bg-green-500/10 text-green-400'
                                        : 'bg-red-500/10 text-red-400'
                                        }`}>
                                        {item.type}
                                      </span>
                                    </td>
                                    <td className="p-4 text-right">
                                      <button
                                        className="text-yellow-400 hover:text-yellow-300 text-xs font-semibold disabled:opacity-60"
                                        onClick={() => setEditingPnlItem({ ...item, amount: normalizePnlInputValue(item.amount) })}
                                        disabled={isPnlSaving}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="text-red-400 hover:text-red-300 text-xs font-semibold ml-4 disabled:opacity-60"
                                        onClick={() => handleDeletePnlItem(item.id)}
                                        disabled={isPnlSaving}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <form onSubmit={handleAddNewPnlItem} className="mt-4 pt-4 border-t dark:border-slate-800 border-slate-200 flex items-end gap-4">
                        <div className="flex-grow">
                          <label htmlFor="pnl-description" className="block text-sm font-semibold text-slate-500 mb-1">
                            Description <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="pnl-description"
                            name="description"
                            type="text"
                            value={newPnlItem.description}
                            onChange={handleNewPnlItemChange}
                            placeholder="e.g., Loan origination fee"
                            disabled={isPnlSaving}
                            className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                          />
                        </div>
                        <div className="w-32">
                          <label htmlFor="pnl-amount" className="block text-sm font-semibold text-slate-500 mb-1">
                            Amount <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="pnl-amount"
                            name="amount"
                            type="number"
                            step="0.01"
                            value={newPnlItem.amount}
                            onChange={handleNewPnlItemChange}
                            placeholder="0.00"
                            disabled={isPnlSaving}
                            className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                          />
                        </div>
                        <div className="w-32">
                          <label htmlFor="pnl-type" className="block text-sm font-semibold text-slate-500 mb-1">
                            Type
                          </label>
                          <select
                            id="pnl-type"
                            name="type"
                            value={newPnlItem.type}
                            onChange={handleNewPnlItemChange}
                            disabled={isPnlSaving}
                            className="w-full rounded-lg px-3 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900 focus:border-yellow-500 focus:outline-none"
                          >
                            <option value="revenue">Revenue</option>
                            <option value="expense">Expense</option>
                          </select>
                        </div>
                        <button
                          type="submit"
                          className="px-4 py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                          disabled={isPnlSaving || !newPnlItem.description.trim() || !newPnlItem.amount}
                          title={!newPnlItem.description.trim() || !newPnlItem.amount ? "Please fill in all required fields" : ""}
                        >
                          Add
                        </button>
                      </form>

                      <div className="mt-4 pt-4 border-t dark:border-slate-800 border-slate-200">
                        <div className="flex justify-end">
                          <div className="w-full max-w-xs space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Total Revenue:</span>
                              <span className="font-semibold text-green-400">{totalRevenue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Total Expenses:</span>
                              <span className="font-semibold text-red-400">{totalExpenses.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                            </div>
                            <div className="flex justify-between text-base">
                              <span className="dark:text-white text-slate-900 font-bold">Net P&L:</span>
                              <span className="font-bold dark:text-white text-slate-900">{netPnl.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Tab Content: Tasks */}
                {activeTab === "tasks" && (
                  <div className="flex items-center justify-center p-6">
                    <CaseTasks
                      caseId={caseKey}
                      members={teamMembers}
                      currentUser={{ id: 'current-user', name: 'You' }} // Passing a dummy current user since we don't have the auth context here easily, or we could pass it from props if available.
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div >

      {/* Embedded DocuSign Console View */}
      {
        showDocuSignView && (
          <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/98 bg-slate-100/98 p-4">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full h-full max-w-[95vw] max-h-[95vh] shadow-2xl flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold dark:text-white text-slate-900">DocuSign Console</h3>
                    <p className="text-xs text-slate-400">
                      {docuSignAccountInfo?.accountName} • Embedded View
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDocuSignView}
                  className="text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Iframe Container */}
              <div className="flex-1 p-4 overflow-hidden">
                <div className="w-full h-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-lg overflow-hidden relative">
                  {/* Mock DocuSign Console - In production, this would be an iframe */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 space-y-6 overflow-y-auto">
                    <div className="text-center space-y-6 max-w-4xl">
                      <div className="w-24 h-24 mx-auto bg-yellow-600/20 rounded-2xl flex items-center justify-center">
                        <FileText className="w-12 h-12 text-yellow-400" />
                      </div>

                      <div>
                        <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-2">Mock DocuSign Embedded Console</h2>
                        <p className="text-slate-400 text-lg">
                          In production, this iframe loads your actual DocuSign dashboard
                        </p>
                      </div>

                      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-4">
                        <p className="text-xs text-slate-500 font-semibold mb-2">Embedded URL:</p>
                        <p className="text-xs text-slate-400 font-mono break-all">{docuSignEmbedUrl}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 text-left">
                        <div className="bg-yellow-950/30 border border-yellow-800/50 rounded-lg p-6 space-y-3">
                          <h3 className="text-lg font-semibold text-yellow-400">What You Can Do:</h3>
                          <ul className="space-y-2 text-sm text-slate-300">
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>Create new envelopes with templates</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>Upload and send documents</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>Add recipients and routing orders</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>Place signature/initial/date fields</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>Track envelope status in real-time</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-emerald-950/30 border border-emerald-800/50 rounded-lg p-6 space-y-3">
                          <h3 className="text-lg font-semibold text-emerald-400">Live Implementation:</h3>
                          <ul className="space-y-2 text-sm text-slate-300">
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Full DocuSign interface embedded</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Access all your templates & folders</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Manage account settings</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>View reports and analytics</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Never leave the mortgage app</span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="bg-yellow-950/30 border border-yellow-800/50 rounded-lg p-6 text-left">
                        <h3 className="text-lg font-semibold text-yellow-400 mb-3">Integration Steps:</h3>
                        <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">
                          <li>Backend generates a console view URL using DocuSign API</li>
                          <li>URL is returned to frontend with proper authentication</li>
                          <li>Iframe loads the DocuSign console with full functionality</li>
                          <li>User interacts with DocuSign without leaving this modal</li>
                          <li>All actions sync with the lender's DocuSign account</li>
                        </ol>
                      </div>

                      <div className="pt-4">
                        <button
                          onClick={handleCloseDocuSignView}
                          className="px-8 py-3 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors flex items-center gap-2 mx-auto"
                        >
                          Close Console View
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Actual iframe in production would look like this: */}
                  {/* <iframe
                  src={docuSignEmbedUrl}
                  className="w-full h-full border-0"
                  title="DocuSign Embedded Console"
                  allow="camera; microphone; clipboard-read; clipboard-write"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                />*/}
                </div>
              </div>

              {/* Footer Info */}
              <div className="px-6 py-3 border-t dark:border-slate-800 border-slate-200 dark:bg-slate-950/50 bg-slate-50/50 flex-shrink-0">
                <p className="text-xs text-slate-500 text-center">
                  Powered by DocuSign Embedded Views • Fully integrated within your app
                </p>
              </div>
            </div>
          </div>
        )
      }

      {/* Embedded HelloSign Console View */}
      {
        showHelloSignView && (
          <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/98 bg-slate-100/98 p-4">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full h-full max-w-[95vw] max-h-[95vh] shadow-2xl flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold dark:text-white text-slate-900">HelloSign Console</h3>
                    <p className="text-xs text-slate-400">
                      {helloSignAccountInfo?.accountName} • Embedded View
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseHelloSignView}
                  className="text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Iframe Container */}
              <div className="flex-1 p-4 overflow-hidden">
                <div className="w-full h-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-lg overflow-hidden relative">
                  {/* Mock HelloSign Console - In production, this would be an iframe */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 space-y-6 overflow-y-auto">
                    <div className="text-center space-y-6 max-w-4xl">
                      <div className="w-24 h-24 mx-auto bg-yellow-600/20 rounded-2xl flex items-center justify-center">
                        <FileText className="w-12 h-12 text-yellow-400" />
                      </div>

                      <div>
                        <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-2">Mock HelloSign Embedded Console</h2>
                        <p className="text-slate-400 text-lg">
                          In production, this iframe loads your actual HelloSign dashboard
                        </p>
                      </div>

                      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-lg p-4">
                        <p className="text-xs text-slate-500 font-semibold mb-2">Embedded URL:</p>
                        <p className="text-xs text-slate-400 font-mono break-all">{helloSignEmbedUrl}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actual iframe in production would look like this: */}
                  {/* <iframe
                  src={helloSignEmbedUrl}
                  className="w-full h-full border-0"
                  title="HelloSign Embedded Console"
                  allow="camera; microphone; clipboard-read; clipboard-write"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                />*/}
                </div>
              </div>

              {/* Footer Info */}
              <div className="px-6 py-3 border-t dark:border-slate-800 border-slate-200 dark:bg-slate-950/50 bg-slate-50/50 flex-shrink-0">
                <p className="text-xs text-slate-500 text-center">
                  Powered by HelloSign Embedded Views • Fully integrated within your app
                </p>
              </div>
            </div>
          </div>
        )
      }

      {/* Acceleration Confirmation Modal */}
      {isAccelerationModalOpen && accelerationOption && (
        <AccelerationConfirmModal
          option={accelerationOption}
          currentTier={userTier}
          userCredits={userCredits}
          onConfirm={handleAccelerationConfirm}
          onCancel={() => {
            setIsAccelerationModalOpen(false);
            setAccelerationOption(null);
          }}
          loading={isAccelerating}
        />
      )}

      <UnderwritingReportModal
        isOpen={showSampleReport}
        onClose={() => {
          setShowSampleReport(false);
          setAutoDownloadSample(false);
          setReportData(null);
        }}
        autoDownload={autoDownloadSample}
        reportData={reportData}
      />
    </>
  );
};

CaseDetailsModal.propTypes = {
  selectedCase: PropTypes.shape({
    id: PropTypes.string,
    caseNumber: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    loanType: PropTypes.string,
    status: PropTypes.string,
    date: PropTypes.string,
    propertyAddress: PropTypes.string,
    loanAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClose: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onDocumentUpload: PropTypes.func.isRequired,
  uploadMessage: PropTypes.string,
  caseFiles: PropTypes.object,
  isUploading: PropTypes.bool,
  isStatusUpdating: PropTypes.bool,
  handlePreview: PropTypes.func.isRequired,
  aiReports: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    eta: PropTypes.string,
    url: PropTypes.string,
  })),
  aiMessage: PropTypes.string,
  onGenerateAi: PropTypes.func.isRequired,
  isAiGenerating: PropTypes.bool,
  caseComments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    author: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  onAddComment: PropTypes.func.isRequired,
  newComment: PropTypes.string,
  setNewComment: PropTypes.func.isRequired,
  isCommenting: PropTypes.bool,
  showSampleReport: PropTypes.bool,
  setShowSampleReport: PropTypes.func.isRequired,
  autoDownloadSample: PropTypes.bool,
  setAutoDownloadSample: PropTypes.func.isRequired,
  isCaseLoading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onDeleteFile: PropTypes.func.isRequired,
  isDeletingFile: PropTypes.bool,
  fileDeleteTarget: PropTypes.string,
  isCaseHydrating: PropTypes.bool,
  onUpgradePlan: PropTypes.func,
  isAccountSettingsOpen: PropTypes.bool,
  aiPipelineStatus: PropTypes.shape({
    status: PropTypes.string,
    run_id: PropTypes.string,
    started_at: PropTypes.string,
    completed_at: PropTypes.string,
    error_message: PropTypes.string,
  }),
  pipelineRuns: PropTypes.arrayOf(PropTypes.shape({
    run_id: PropTypes.string,
    status: PropTypes.string,
    started_at: PropTypes.string,
    name: PropTypes.string,
    run_name: PropTypes.string,
    pipeline_name: PropTypes.string,
    display_name: PropTypes.string,
    error_message: PropTypes.string,
  })),
  caseError: PropTypes.string,
  userTier: PropTypes.string,
  userCredits: PropTypes.number,
  onRefreshCredits: PropTypes.func,
  onCaseUpdate: PropTypes.func,
};

export default CaseDetailsModal;
